import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Box, CircularProgress, Typography } from '@mui/material';
import { PdfToolbar, PdfSidebar, PdfAnnotatePanel, PdfCanvas, PdfPage, usePdfView } from '../../../components/PdfToolbar';
import './PayReceiveFormat1PrintPage.css';

// A5 (portrait, same size as the Journal Voucher) Bank / Cash Payment & Receipt voucher — format 1.
// Data comes from GET /api/get-payReceive-pdf-data (master + billwise + company).

// Bill rows per sheet. Every sheet is padded with blank rows up to this count so all pages keep
// the same table height; the grand total / remark / signatures block always shows on every sheet.
const ROWS_PER_PAGE = 12;

// Header printed in the title bar, chosen by the voucher's BookName.
const VOUCHER_TITLES = {
    BKP: 'BANK PAYMENT VOUCHER',
    BKR: 'BANK RECEIPT VOUCHER',
    CBP: 'CASH PAYMENT VOUCHER',
    CBR: 'CASH RECEIPT VOUCHER',
};

const ONES = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE', 'TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
const TENS = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

const below100 = (n) => (n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]}${n % 10 ? ` ${ONES[n % 10]}` : ''}`);
const below1000 = (n) => {
    const h = Math.floor(n / 100);
    const r = n % 100;
    return `${h ? `${ONES[h]} HUNDRED` : ''}${h && r ? ' ' : ''}${r ? below100(r) : ''}`;
};
// Indian numbering (Crore / Lakh / Thousand)
const intToWords = (num) => {
    if (num === 0) return 'ZERO';
    const parts = [];
    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const rest = num % 1000;
    if (crore) parts.push(`${below100(crore)} CRORE`);
    if (lakh) parts.push(`${below100(lakh)} LAKH`);
    if (thousand) parts.push(`${below100(thousand)} THOUSAND`);
    if (rest) parts.push(below1000(rest));
    return parts.join(' ');
};
const amountInWords = (value) => {
    const total = Math.round((Number(value) || 0) * 100);
    const rupees = Math.floor(total / 100);
    const paise = total % 100;
    let words = `RUPEES : ${intToWords(rupees)}`;
    if (paise) words += ` AND ${below100(paise)} PAISE`;
    return `${words} ONLY`;
};

const toNum = (v) => (v === null || v === undefined || v === '' ? 0 : Number(v) || 0);
const formatAmt = (v) => {
    const n = toNum(v);
    return n === 0 ? '' : n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
// "2026-10-31" / "2026-10-31 00:00:00" -> "31-10-26" (string split, no Date parsing, so no timezone shift)
const formatDate = (value) => {
    if (!value) return '';
    const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}-${m[2]}-${m[1].slice(2)}` : String(value);
};

export default function PayReceiveFormat1PrintPage({ connectionString }) {
    const { receiptId } = useParams();
    const [searchParams] = useSearchParams();
    const companyId = searchParams.get('companyId');
    const type = searchParams.get('type');

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        axios
            .get(`${connectionString}/api/get-payReceive-pdf-data`, { params: { receiptId, companyId, type } })
            .then((res) => { if (!cancelled) setData(res.data); })
            .catch((err) => {
                console.error('Failed to load receipt/payment for print:', err);
                if (!cancelled) setError('Failed to load receipt/payment.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [receiptId, companyId, type, connectionString]);

    const { rows, totalBill, totalPaid, totalOs } = useMemo(() => {
        const lines = Array.isArray(data?.billwise) ? data.billwise : [];
        const mapped = lines.map((l) => {
            const billAmt = toNum(l.Inv_BillAmt);
            const paid = toNum(l.PaidAmt);
            // OSAMT is the outstanding before this payment/receipt; the sheet shows what is still left after it.
            const os = Math.max(toNum(l.OSAMT) - paid, 0);
            return {
                key: l.Id,
                date: formatDate(l.Inv_BillDate),
                billNo: l.Inv_BillNumber || '',
                bookName: l.BookName || '',
                billAmt,
                paid,
                os,
            };
        });
        return {
            rows: mapped,
            totalBill: mapped.reduce((s, r) => s + r.billAmt, 0),
            totalPaid: mapped.reduce((s, r) => s + r.paid, 0),
            totalOs: mapped.reduce((s, r) => s + r.os, 0),
        };
    }, [data]);

    const handlePrint = () => window.print();

    // Zoom / fit / page navigation / annotate / download state for the toolbar (hook must run before any early return).
    const view = usePdfView(Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE)));

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !data?.master) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <Typography color="error">{error || 'Receipt/Payment not found.'}</Typography>
            </Box>
        );
    }

    const { master, company } = data;
    const bookName = String(master.BookName || '').toUpperCase();
    const title = VOUCHER_TITLES[bookName] || '';
    const isBank = bookName === 'BKP' || bookName === 'BKR';
    const totalAmount = toNum(master.PaidAmt) || toNum(master.Amount);

    const partyAddressLines = [
        master.PartyAddress,
        master.PartyArea,
        master.PartyCity,
        [master.PartyState, master.PartyPincode].filter(Boolean).join(' - '),
    ].filter((v) => v && String(v).trim());

    const pages = [];
    for (let i = 0; i < Math.max(rows.length, 1); i += ROWS_PER_PAGE) {
        pages.push(rows.slice(i, i + ROWS_PER_PAGE));
    }

    return (
        <>
            <PdfToolbar pageCount={pages.length} view={view} onPrint={handlePrint} downloadFilename={`${title || 'Voucher'}-${master.ReceiptNumber || receiptId}.pdf`.replace(/[\\/:*?"<>|]/g, '-')} />
            <PdfSidebar view={view} />
            <PdfAnnotatePanel view={view} />

            <PdfCanvas view={view} className="pr1-canvas">
                {pages.map((pageRows, pageIndex) => {
                    const blanks = Array.from({ length: ROWS_PER_PAGE - pageRows.length });
                    return (
                        <PdfPage view={view} index={pageIndex} className="pr1-sheet" key={pageIndex}>
                            <div className="pr1-header">
                                <div className="pr1-company-name">{company?.CompanyName || ''}</div>
                                {company?.Address && <div className="pr1-company-line">{company.Address}</div>}
                                {company?.MobileNo && <div className="pr1-company-line">MO. : {company.MobileNo}</div>}
                                {company?.EmailId && <div className="pr1-company-line">E-MAIL : {company.EmailId}</div>}
                            </div>

                            <div className="pr1-box">
                                <div className="pr1-title-row">
                                    <span className="pr1-title-spacer" />
                                    <span className="pr1-title">{title}</span>
                                    <span className="pr1-page-no">Page {pageIndex + 1} of {pages.length}</span>
                                </div>

                                <div className="pr1-to-row">
                                    <div className="pr1-to-left">
                                        <div className="pr1-to-name"><span className="pr1-to-label">To,</span>{master.PartyName || ''}</div>
                                        <div className="pr1-to-address">
                                            {partyAddressLines.map((line, i) => <div key={i}>{line}</div>)}
                                        </div>
                                    </div>
                                    <div className="pr1-to-right">
                                        <div><span className="pr1-k">ReceiptNo</span><span className="pr1-c">:-</span><span>{master.ReceiptNumber || ''}</span></div>
                                        <div><span className="pr1-k">Date</span><span className="pr1-c">:-</span><span>{formatDate(master.ReceiptDate)}</span></div>
                                        <div><span className="pr1-k">ChequeNo</span><span className="pr1-c">:-</span><span>{master.ChequeNo || ''}</span></div>
                                    </div>
                                </div>

                                <div className="pr1-bank-row">
                                    <span>{isBank ? 'Bank Name:' : 'Cash A/C:'}</span>
                                    <span className="pr1-bank-name">{master.BankName || ''}</span>
                                </div>

                                <div className="pr1-amount-row">
                                    <span>Amount :- {formatAmt(master.Amount)}</span>
                                    <span>T.D.S:- {formatAmt(master.TDSAmt)}</span>
                                    <span>Comm.:- {formatAmt(master.BankComm)}</span>
                                    <span>RndOff:- {formatAmt(master.RoundOffAmt)}</span>
                                </div>

                                <div className="pr1-total-row">
                                    <div><span className="pr1-total-k">Total Amount</span><span className="pr1-c">:-</span><span className="pr1-total-v">{formatAmt(totalAmount)}</span></div>
                                    <div><span className="pr1-total-k">Rupees In Word :-</span><span className="pr1-words">{amountInWords(totalAmount)}</span></div>
                                </div>

                                <table className="pr1-table">
                                    <colgroup>
                                        <col style={{ width: '12%' }} />
                                        <col style={{ width: '22%' }} />
                                        <col style={{ width: '13%' }} />
                                        <col style={{ width: '18%' }} />
                                        <col style={{ width: '18%' }} />
                                        <col style={{ width: '17%' }} />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Bill No</th>
                                            <th>BookName</th>
                                            <th className="pr1-r">Bill Amt</th>
                                            <th className="pr1-r">PaidAmt</th>
                                            <th className="pr1-r">O.S.Amt</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageRows.map((r) => (
                                            <tr key={r.key}>
                                                <td className="pr1-c-mid">{r.date}</td>
                                                <td className="pr1-c-mid">{r.billNo}</td>
                                                <td className="pr1-c-mid">{r.bookName}</td>
                                                <td className="pr1-r">{formatAmt(r.billAmt)}</td>
                                                <td className="pr1-r">{formatAmt(r.paid)}</td>
                                                <td className="pr1-r">{formatAmt(r.os)}</td>
                                            </tr>
                                        ))}
                                        {blanks.map((_, i) => (
                                            <tr key={`blank-${i}`}>
                                                <td>&nbsp;</td><td /><td /><td /><td /><td />
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan={3} className="pr1-grand-label">Grand Total:</td>
                                            <td className="pr1-r">{formatAmt(totalBill)}</td>
                                            <td className="pr1-r">{formatAmt(totalPaid)}</td>
                                            <td className="pr1-r">{formatAmt(totalOs)}</td>
                                        </tr>
                                    </tfoot>
                                </table>

                                <div className="pr1-footer">
                                    <div className="pr1-footer-top">
                                        <span className="pr1-remark"><strong>Remark:-</strong> {master.Remark || ''}</span>
                                        <span className="pr1-for">For,{company?.CompanyName || ''}</span>
                                    </div>
                                    <div className="pr1-footer-bottom">
                                        <span>RECEIVER' S SIGNATURE</span>
                                        <span>(Authorised Signatory)</span>
                                    </div>
                                </div>
                            </div>
                        </PdfPage>
                    );
                })}
            </PdfCanvas>
        </>
    );
}
